import { useState, type FormEvent } from 'react'
import { useTwoFactor } from '@features/auth/hooks/useTwoFactor'
import { Notice } from '@shared/ui/Notice'
import { QrCode } from '@shared/ui/QrCode'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * `#settings/2fa` の 2FA セクション (Phase 4 #44 frontend wiring / B5 で QR 対応)。
 *
 *   - 未設定: 「有効化」 → setup → QR コード + 手動シークレット + コード入力 → verify-enable
 *   - 設定中 (pending): 入力フォーム再表示で完了
 *   - 有効中: 「解除」 + 「リカバリコード再生成」
 *   - verify-enable / regenerate 成功時に **平文 recovery codes** を 1 回限り表示
 *
 * QR コードは `@utils/qrcode` の自前エンコーダでクライアント内生成する
 * (otpauth URI = TOTP シークレットを含むため外部 API には渡さない)。
 */

type Mode = 'idle' | 'setup' | 'disable' | 'regenerate'

type DisableMethod = 'totp' | 'email'

export const TwoFactorSection = (): JSX.Element => {
  const { t } = useTranslation()
  const tf = useTwoFactor()
  const [mode, setMode] = useState<Mode>('idle')
  const [code, setCode] = useState('')
  // disable mode 内の sub-mode: TOTP (Authenticator) vs Email (一時コード受信)
  const [disableMethod, setDisableMethod] = useState<DisableMethod>('totp')
  // email method で 6 桁 一時コードを送信済か (true なら code 入力 UI を出す)
  const [emailCodeSent, setEmailCodeSent] = useState(false)

  const submitVerifyEnable = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault()

    const ok = await tf.verifyEnable(code)
    if (ok) {
      setCode('')
      setMode('idle')
    }
  }

  const submitDisable = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault()

    const ok =
      disableMethod === 'email' ? await tf.disableByEmailCode(code) : await tf.disable(code)

    if (ok) {
      setCode('')
      setMode('idle')
      setDisableMethod('totp')
      setEmailCodeSent(false)
    }
  }

  const handleRequestDisableEmail = async (): Promise<void> => {
    setEmailCodeSent(true)
    setCode('')
    await tf.requestDisableEmailCode()
  }

  const submitRegenerate = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault()

    const ok = await tf.regenerate(code)
    if (ok) {
      setCode('')
      setMode('idle')
    }
  }

  const startSetupFlow = async (): Promise<void> => {
    await tf.startSetup()
    setMode('setup')
    setCode('')
  }

  const cancelSetup = (): void => {
    tf.cancelSetup()
    setMode('idle')
    setCode('')
  }

  const closeRecoveryCodes = (): void => {
    tf.clearRecoveryCodes()
  }

  // ── Recovery codes 1 回限り表示 (verify-enable / regenerate 成功時) ──
  if (tf.recoveryCodes !== null) {
    return (
      <div data-testid="twofa-recovery-codes">
        <Notice tone="warn" ico="alert">
          {t('settings.2fa.codes_intro')}
        </Notice>
        <ul className="twofa-codes-list">
          {tf.recoveryCodes.map(c => (
            <li key={c} className="twofa-code mono">
              {c}
            </li>
          ))}
        </ul>
        <button type="button" className="btn btn-primary" onClick={closeRecoveryCodes}>
          {t('settings.2fa.codes_close')}
        </button>
      </div>
    )
  }

  // ── Setup flow (otpauth URI + secret + code 入力) ──
  if (mode === 'setup' && tf.setupData !== null) {
    return (
      <form onSubmit={ev => void submitVerifyEnable(ev)} data-testid="twofa-setup-form">
        <Notice tone="info">{t('settings.2fa.setup_intro')}</Notice>
        <div className="field twofa-qr-field">
          <label className="field-label">{t('settings.2fa.setup_qr_label')}</label>
          <div className="twofa-qr" data-testid="twofa-qr">
            <QrCode value={tf.setupData.otpAuthUri} label={t('settings.2fa.setup_qr_label')} />
          </div>
          <p className="twofa-qr-hint">{t('settings.2fa.setup_qr_hint')}</p>
        </div>
        <div className="field">
          <label className="field-label">{t('settings.2fa.setup_secret_label')}</label>
          <code className="twofa-secret mono">{tf.setupData.secretBase32}</code>
        </div>
        <div className="field">
          <label className="field-label" htmlFor="twofa-enable-code">
            {t('settings.2fa.setup_code_label')}
          </label>
          <input
            id="twofa-enable-code"
            className="input lg"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={code}
            onChange={ev => setCode(ev.target.value)}
            disabled={tf.isSubmitting}
          />
        </div>
        {tf.errorKey !== null && (
          <p className="form-error" role="alert">
            {t(tf.errorKey)}
          </p>
        )}
        <div className="twofa-form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={cancelSetup}
            disabled={tf.isSubmitting}
          >
            {t('settings.2fa.setup_cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={tf.isSubmitting}>
            {t('settings.2fa.setup_submit')}
          </button>
        </div>
      </form>
    )
  }

  // ── Disable flow ──
  if (mode === 'disable') {
    // submit ボタン活性化判定: code が 6 桁数字。email method の Step 1 (送信前) は disable。
    const canSubmit =
      code.length === 6 &&
      !tf.isSubmitting &&
      (disableMethod === 'totp' || (disableMethod === 'email' && emailCodeSent))

    return (
      <form onSubmit={ev => void submitDisable(ev)} data-testid="twofa-disable-form">
        <Notice tone="warn" ico="alert">
          {disableMethod === 'totp'
            ? t('settings.2fa.disable_intro')
            : emailCodeSent
              ? t('settings.2fa.disable_email_sent')
              : t('settings.2fa.disable_email_intro')}
        </Notice>

        {/* 解除方法: Authenticator (TOTP) vs Email (一時コード) */}
        <div
          className="seg-group twofa-disable-method-seg"
          role="radiogroup"
          aria-label={t('settings.2fa.disable_method_label')}
        >
          {(['totp', 'email'] as const).map(m => {
            const active = disableMethod === m

            return (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={active}
                className={`seg-btn${active ? ' seg-btn-active' : ''}`}
                onClick={() => {
                  if (active) return
                  setDisableMethod(m)
                  setCode('')
                  setEmailCodeSent(false)
                }}
                disabled={tf.isSubmitting}
                data-testid={`twofa-disable-method-${m}`}
              >
                {m === 'totp'
                  ? t('settings.2fa.disable_method_totp')
                  : t('settings.2fa.disable_method_email')}
              </button>
            )
          })}
        </div>

        {/* email method の Step 1: 送信ボタン (compact + mail icon、Notice の下に控えめに配置) */}
        {disableMethod === 'email' && !emailCodeSent && (
          <div className="twofa-disable-email-cta">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void handleRequestDisableEmail()}
              disabled={tf.isSubmitting}
              data-testid="twofa-disable-email-request"
            >
              <Icon name="mail" aria-hidden="true" />
              {t('settings.2fa.disable_email_send_button')}
            </button>
          </div>
        )}

        {/* TOTP / email Step 2 共通: 6 桁 code 入力 */}
        {!(disableMethod === 'email' && !emailCodeSent) && (
          <div className="field">
            <label className="field-label" htmlFor="twofa-disable-code">
              {t('settings.2fa.setup_code_label')}
            </label>
            <input
              id="twofa-disable-code"
              className="input lg"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={code}
              onChange={ev => setCode(ev.target.value.replace(/\D/gu, ''))}
              disabled={tf.isSubmitting}
            />
          </div>
        )}

        {tf.errorKey !== null && (
          <p className="form-error" role="alert">
            {t(tf.errorKey)}
          </p>
        )}
        <div className="twofa-form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setMode('idle')
              setCode('')
              setDisableMethod('totp')
              setEmailCodeSent(false)
            }}
            disabled={tf.isSubmitting}
          >
            {t('settings.2fa.setup_cancel')}
          </button>
          <button type="submit" className="btn btn-danger" disabled={!canSubmit}>
            {t('settings.2fa.disable_submit')}
          </button>
        </div>
      </form>
    )
  }

  // ── Regenerate flow ──
  if (mode === 'regenerate') {
    return (
      <form onSubmit={ev => void submitRegenerate(ev)} data-testid="twofa-regenerate-form">
        <Notice tone="info">{t('settings.2fa.regenerate_intro')}</Notice>
        <div className="field">
          <label className="field-label" htmlFor="twofa-regenerate-code">
            {t('settings.2fa.setup_code_label')}
          </label>
          <input
            id="twofa-regenerate-code"
            className="input lg"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={code}
            onChange={ev => setCode(ev.target.value)}
            disabled={tf.isSubmitting}
          />
        </div>
        {tf.errorKey !== null && (
          <p className="form-error" role="alert">
            {t(tf.errorKey)}
          </p>
        )}
        <div className="twofa-form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setMode('idle')
              setCode('')
            }}
            disabled={tf.isSubmitting}
          >
            {t('settings.2fa.setup_cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={tf.isSubmitting}>
            {t('settings.2fa.regenerate_submit')}
          </button>
        </div>
      </form>
    )
  }

  // ── Idle: status 表示 + 操作ボタン ──
  if (tf.loading) {
    return <p>{t('settings.2fa.title')}…</p>
  }

  const enabled = tf.status?.enabled ?? false
  const remaining = tf.status?.recoveryCodesRemaining ?? 0

  return (
    <div data-testid="twofa-idle">
      <div className="card settings-security-item">
        <div className="card-row-flex">
          <div>
            <div className="settings-item-label">{t('settings.2fa.totp_label')}</div>
            <div className="settings-item-sub">{t('settings.2fa.totp_sub')}</div>
          </div>
          <div className="settings-2fa-actions">
            {enabled ? (
              <>
                <span className="tag good">
                  <Icon name="check" aria-hidden="true" width={12} height={12} />
                  {t('settings.2fa.enabled')}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setMode('disable')}
                >
                  {t('settings.2fa.disable_submit')}
                </button>
              </>
            ) : (
              <>
                <span className="tag">{t('settings.2fa.disabled')}</span>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => void startSetupFlow()}
                  disabled={tf.isSubmitting}
                >
                  {t('settings.2fa.enable')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {enabled && (
        <div className="card settings-security-item">
          <div className="card-row-flex">
            <div>
              <div className="settings-item-label">{t('settings.2fa.recovery_label')}</div>
              <div className="settings-item-sub">
                {t('settings.2fa.recovery_sub')} ・{' '}
                {t('settings.2fa.recovery_remaining', { count: remaining })}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setMode('regenerate')}
            >
              {t('settings.2fa.recovery_manage')}
            </button>
          </div>
        </div>
      )}

      {tf.errorKey !== null && (
        <Notice tone="danger" ico="alert">
          {t(tf.errorKey)}
        </Notice>
      )}
    </div>
  )
}
